import {
  type ChaveDaFaixa,
  type MotivoDeBloqueio,
  type StatusDaCotacao,
  type StatusDaWaitlist,
  type TipoDeExtra,
  type TipoDeLinha,
  type TipoDePrestador,
  ROTULO_DA_FAIXA,
  ROTULO_DO_MOTIVO_DE_BLOQUEIO,
  ROTULO_DO_STATUS_DE_COTACAO,
  ROTULO_DO_STATUS_DE_WAITLIST,
  ROTULO_DO_TIPO_DE_EXTRA,
  ROTULO_DO_TIPO_DE_LINHA,
  ROTULO_DO_TIPO_DE_PRESTADOR,
} from '@locaveri/shared'
import {
  BellRing,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  CarFront,
  CircleCheck,
  CircleDot,
  CircleQuestionMark,
  CircleSlash,
  CircleX,
  ClipboardCheck,
  Clock,
  CornerDownLeft,
  Gauge,
  Hourglass,
  type LucideIcon,
  Package,
  Ruler,
  ShieldCheck,
  Sparkles,
  Sun,
  Tag,
  TicketPercent,
  TrendingUp,
  TriangleAlert,
  Truck,
  UserPlus,
  Wrench,
} from 'lucide-react'
import type { ReactElement } from 'react'

import { Badge } from '@/components/ui/badge'

/**
 * Selos de preço, cotação e disponibilidade.
 *
 * ---------------------------------------------------------------------------
 * COR NUNCA COMUNICA SOZINHA
 * ---------------------------------------------------------------------------
 * Regra do CLAUDE.md, e aqui ela decide dinheiro: quem não distingue verde de
 * âmbar precisa saber se a cotação ainda VALE e se o carro está livre. Todo selo
 * tem cor + ÍCONE + RÓTULO, e o rótulo vem de `@locaveri/shared` — a mesma
 * string que a API e o app usam. Redigitar "Convertida em reserva" aqui seria a
 * divergência do dia em que o status ganhar um valor novo.
 *
 * ---------------------------------------------------------------------------
 * AMARELO É MARCA, NÃO AVISO — E NESTA TELA ISSO É CRÍTICO
 * ---------------------------------------------------------------------------
 * Nenhum selo daqui usa a variante `brand`. A tela de preço é uma grade de
 * números: se cada faixa, cada tipo de linha e cada multiplicador virasse
 * amarelo, a página inteira ficaria amarela e o CTA — o botão que efetivamente
 * salva a tabela — perderia o único lugar em que o olho deve parar. Aviso é
 * `warning` (âmbar), que é visualmente distinto do amarelo do logo.
 *
 * ---------------------------------------------------------------------------
 * O QUE É CLASSIFICAÇÃO E O QUE É ESTADO
 * ---------------------------------------------------------------------------
 * Faixa, tipo de extra, tipo de linha e tipo de prestador CLASSIFICAM: aparecem
 * em toda linha da tabela e não pedem reação nenhuma — vão de `outline` e
 * `neutral`, e o peso visual vem do ícone. Status de cotação, status de lista de
 * espera e motivo de bloqueio são ESTADO: alguém decide algo a partir deles, e é
 * só aí que a cor semântica entra.
 */

// ---------------------------------------------------------------------------
// Faixa de duração
// ---------------------------------------------------------------------------

/**
 * A faixa de duração da tarifa (`1 a 2 diárias`, `30 diárias ou mais`).
 *
 * `outline` e não `neutral`: a faixa aparece cinco vezes por categoria na grade
 * de tarifas, e um preenchimento por selo transformaria a tabela num tabuleiro.
 * O rótulo vem de `FAIXAS_DE_DURACAO`, via `ROTULO_DA_FAIXA` — uma faixa nova no
 * motor não pode depender de alguém lembrar deste arquivo.
 */
export function BadgeDeFaixa({ faixa }: { readonly faixa: ChaveDaFaixa }): ReactElement {
  return (
    <Badge variant="outline" title="Faixa de duração da locação">
      <CalendarRange aria-hidden="true" />
      {ROTULO_DA_FAIXA[faixa]}
    </Badge>
  )
}

// ---------------------------------------------------------------------------
// Tipo de extra
// ---------------------------------------------------------------------------

/**
 * O ícone de cada tipo de extra.
 *
 * `MOTORISTA` e `SEGURANCA` ganham ícones deliberadamente distintos dos demais:
 * são os dois tipos que dependem de GENTE livre na escala, e a cotação recusa o
 * extra quando não há prestador — quem lê a lista precisa reconhecê-los de
 * relance para saber onde olhar quando um extra some do orçamento.
 */
const ICONE_DO_TIPO_DE_EXTRA: Readonly<Record<TipoDeExtra, LucideIcon>> = {
  PROTECAO: ShieldCheck,
  CONDUTOR_ADICIONAL: UserPlus,
  ACESSORIO: Package,
  ENTREGA: Truck,
  DEVOLUCAO: CornerDownLeft,
  MOTORISTA: CarFront,
  SEGURANCA: ShieldCheck,
  KM_EXTRA: Gauge,
  OUTRO: Tag,
}

/**
 * A categoria funcional do extra. É classificação, não estado — daí `neutral`
 * para todos, sem exceção colorida: pintar `PROTECAO` de verde sugeriria que o
 * extra está ativo, que é outro campo.
 */
export function BadgeDeTipoDeExtra({ tipo }: { readonly tipo: TipoDeExtra }): ReactElement {
  const Icone = ICONE_DO_TIPO_DE_EXTRA[tipo]

  return (
    <Badge variant="neutral">
      <Icone aria-hidden="true" />
      {ROTULO_DO_TIPO_DE_EXTRA[tipo]}
    </Badge>
  )
}

// ---------------------------------------------------------------------------
// Tipo de linha do breakdown
// ---------------------------------------------------------------------------

/**
 * Cada linha do detalhamento da cotação, com ícone próprio.
 *
 * Oito tipos e oito ícones diferentes de propósito: o breakdown é lido para
 * conferir de onde saiu o total ("por que deu R$ 4.200?"), e ícone repetido
 * obrigaria a ler o rótulo linha a linha. `CUPOM` é o único que costuma vir com
 * valor NEGATIVO — o breakdown SOMA, não subtrai —, e por isso é o único com
 * variante própria: `info`, para o olho achar o desconto na coluna sem que ele
 * pareça um erro (o que `danger` sugeriria) nem um acerto (o que `success`
 * sugeriria; desconto é decisão comercial, não conquista).
 */
const LINHA = {
  DIARIAS: { variante: 'neutral', icone: CalendarDays },
  SAZONALIDADE: { variante: 'neutral', icone: Sun },
  DIA_DA_SEMANA: { variante: 'neutral', icone: CalendarClock },
  LIMITE: { variante: 'warning', icone: Ruler },
  ANTECEDENCIA: { variante: 'neutral', icone: Hourglass },
  OCUPACAO: { variante: 'neutral', icone: TrendingUp },
  EXTRA: { variante: 'neutral', icone: Package },
  CUPOM: { variante: 'info', icone: TicketPercent },
} as const satisfies Readonly<Record<TipoDeLinha, unknown>>

/**
 * `LIMITE` é âmbar porque não é uma regra que alguém escreveu: é o teto ou o
 * piso da categoria tendo CORTADO o que as regras calcularam. A linha aparece
 * justamente quando o preço bateu no limite — e um multiplicador que não surtiu
 * efeito é a primeira coisa que o gestor precisa ver ao conferir a conta.
 */
export function BadgeDeTipoDeLinha({ tipo }: { readonly tipo: TipoDeLinha }): ReactElement {
  const { variante, icone: Icone } = LINHA[tipo]

  return (
    <Badge variant={variante}>
      <Icone aria-hidden="true" />
      {ROTULO_DO_TIPO_DE_LINHA[tipo]}
    </Badge>
  )
}

// ---------------------------------------------------------------------------
// Status da cotação
// ---------------------------------------------------------------------------

/**
 * O estado de uma cotação emitida.
 *
 * `EXPIRADA` é `neutral` e não `warning` de propósito: prazo vencido não é
 * problema a resolver — é o comportamento normal de um preço com validade, e
 * toda cotação de ontem está assim. Pintar de âmbar encheria a listagem de
 * alertas que ninguém pode nem deve atender, e o âmbar perderia o significado
 * onde ele importa. Quem exige ação é a cotação ABERTA, que ainda dá para
 * converter — daí ela ser a única com cor viva.
 */
const STATUS_DA_COTACAO = {
  ABERTA: { variante: 'info', icone: CircleDot },
  EXPIRADA: { variante: 'neutral', icone: CircleSlash },
  CONVERTIDA: { variante: 'success', icone: CircleCheck },
} as const satisfies Readonly<Record<StatusDaCotacao, unknown>>

export function BadgeDeStatusDaCotacao({
  status,
}: {
  readonly status: StatusDaCotacao
}): ReactElement {
  const { variante, icone: Icone } = STATUS_DA_COTACAO[status]

  return (
    <Badge variant={variante}>
      <Icone aria-hidden="true" />
      {ROTULO_DO_STATUS_DE_COTACAO[status]}
    </Badge>
  )
}

// ---------------------------------------------------------------------------
// Status da lista de espera
// ---------------------------------------------------------------------------

/**
 * O estado de um interesse na fila.
 *
 * `EXPIRADO` é o único vermelho da lista, e é o único que acusa a LOCADORA:
 * alguém pediu um carro, ninguém retornou, e a data passou — venda perdida por
 * inação. `DESISTIU` é cinza porque o cliente decidiu, e não há nada a corrigir.
 * `NOTIFICADO` é âmbar porque a bola está com a locadora: avisamos que havia
 * carro e ainda não sabemos a resposta.
 */
const STATUS_DA_WAITLIST = {
  AGUARDANDO: { variante: 'info', icone: Clock },
  NOTIFICADO: { variante: 'warning', icone: BellRing },
  CONVERTIDO: { variante: 'success', icone: CircleCheck },
  DESISTIU: { variante: 'neutral', icone: CircleSlash },
  EXPIRADO: { variante: 'danger', icone: CircleX },
} as const satisfies Readonly<Record<StatusDaWaitlist, unknown>>

export function BadgeDeStatusDaWaitlist({
  status,
}: {
  readonly status: StatusDaWaitlist
}): ReactElement {
  const { variante, icone: Icone } = STATUS_DA_WAITLIST[status]

  return (
    <Badge variant={variante}>
      <Icone aria-hidden="true" />
      {ROTULO_DO_STATUS_DE_WAITLIST[status]}
    </Badge>
  )
}

// ---------------------------------------------------------------------------
// Motivo do bloqueio
// ---------------------------------------------------------------------------

/**
 * Por que o recurso está indisponível.
 *
 * `ALOCACAO` e `RESERVA` são cinza e carregam explicação no `title` porque não
 * são escolha de ninguém: nascem da reserva, a operação não os cria nem os
 * remove pelo painel, e a API recusa (422) quem tentar. Um selo colorido neles
 * convidaria alguém a "liberar o carro" — que não cancelaria a reserva, só faria
 * o sistema vender de novo um carro que já tem dono.
 *
 * `SINISTRO` é o único vermelho: é o que tira o carro de circulação por tempo
 * indeterminado, e não pode se parecer com uma limpeza de duas horas.
 */
const MOTIVO_DO_BLOQUEIO = {
  MANUTENCAO: { variante: 'warning', icone: Wrench, explicacao: '' },
  LIMPEZA: { variante: 'info', icone: Sparkles, explicacao: '' },
  VISTORIA: { variante: 'info', icone: ClipboardCheck, explicacao: '' },
  SINISTRO: { variante: 'danger', icone: TriangleAlert, explicacao: '' },
  ALOCACAO: {
    variante: 'neutral',
    icone: CalendarCheck,
    explicacao: 'Escrito pela reserva. Não é editável pela operação.',
  },
  RESERVA: {
    variante: 'neutral',
    icone: CalendarClock,
    explicacao: 'Escrito pela reserva. Não é editável pela operação.',
  },
  INDISPONIBILIDADE: { variante: 'neutral', icone: CircleSlash, explicacao: '' },
  OUTRO: { variante: 'neutral', icone: CircleQuestionMark, explicacao: '' },
} as const satisfies Readonly<Record<MotivoDeBloqueio, unknown>>

export function BadgeDeMotivoDeBloqueio({
  motivo,
}: {
  readonly motivo: MotivoDeBloqueio
}): ReactElement {
  const { variante, icone: Icone, explicacao } = MOTIVO_DO_BLOQUEIO[motivo]

  return (
    <Badge variant={variante} {...(explicacao.length > 0 ? { title: explicacao } : {})}>
      <Icone aria-hidden="true" />
      {ROTULO_DO_MOTIVO_DE_BLOQUEIO[motivo]}
    </Badge>
  )
}

// ---------------------------------------------------------------------------
// Tipo de prestador
// ---------------------------------------------------------------------------

const ICONE_DO_TIPO_DE_PRESTADOR: Readonly<Record<TipoDePrestador, LucideIcon>> = {
  MOTORISTA: CarFront,
  SEGURANCA: ShieldCheck,
}

/**
 * Motorista ou segurança. `outline`, como toda classificação: a cor aqui diria
 * algo sobre a pessoa estar livre, e quem responde isso é a escala, não o
 * cadastro.
 */
export function BadgeDeTipoDePrestador({ tipo }: { readonly tipo: TipoDePrestador }): ReactElement {
  const Icone = ICONE_DO_TIPO_DE_PRESTADOR[tipo]

  return (
    <Badge variant="outline">
      <Icone aria-hidden="true" />
      {ROTULO_DO_TIPO_DE_PRESTADOR[tipo]}
    </Badge>
  )
}
